import {
  Badge,
  Button,
  Card,
  CardContent,
  CardFooter,
  DaySelector,
  Icon,
  IconButton,
} from "@playerelite/ui";
import {
  ContentTypeBadge,
  GamingContentBadge,
  LinkedPromotionBadge,
} from "../../components/AttractorContentBadges";
import { KioskAttractor } from "../../types";
import {
  campaignStatus,
  daysFromVisibility,
  daysLeft,
  formatDateRange,
  isVideoUrl,
} from "./campaign-utils";

const STATUS_BADGE = {
  active: { label: "Active", variant: "success", icon: "success" },
  future: { label: "Future", variant: "info", icon: "clock" },
  expired: { label: "Expired", variant: "danger", icon: "error" },
} as const;

export const attractorTitle = (attractor: KioskAttractor) =>
  `Attractor #${attractor.attractorId}`;

interface Props {
  attractor: KioskAttractor;
  now: number;
  onEdit: (attractor: KioskAttractor) => void;
  onDelete: (attractor: KioskAttractor) => void;
}

/** One kiosk attractor, laid out as the Storybook CampaignCard. */
const AttractorCampaignCard = ({ attractor, now, onEdit, onDelete }: Props) => {
  const title = attractorTitle(attractor);
  const status = campaignStatus(attractor, now);
  const statusBadge = STATUS_BADGE[status];

  return (
    <Card
      className="flex flex-col overflow-hidden"
      data-testid={`attractor-${attractor.attractorId}`}
    >
      {/* Kiosk artwork is portrait, so it is contained rather than cropped to the card. */}
      <div className="relative h-64 bg-surface-sunken">
        {isVideoUrl(attractor.imageUrl) ? (
          <video
            className="h-full w-full object-contain"
            src={attractor.imageUrl}
            autoPlay
            loop
            muted
            playsInline
            aria-label={`${title} media`}
          />
        ) : (
          <img
            className="h-full w-full object-contain"
            src={attractor.imageUrl}
            alt={`${title} artwork`}
            loading="lazy"
          />
        )}
        <div className="absolute inset-x-0 top-0 flex items-start justify-between p-2">
          <Badge
            variant={statusBadge.variant}
            startIcon={<Icon name={statusBadge.icon} size="xs" />}
          >
            {statusBadge.label}
          </Badge>
          {status === "active" ? (
            <Badge
              variant="overlay"
              startIcon={<Icon name="clock" size="xs" />}
            >
              {daysLeft(attractor.endDateEpoch, now)}d left
            </Badge>
          ) : null}
        </div>
        {attractor.isGamingContent ? (
          <div className="absolute bottom-2 left-2">
            <GamingContentBadge />
          </div>
        ) : null}
      </div>

      <CardContent className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          {/* h2, not h3: the page heading is the h1, so a card title at h3 skips a level. */}
          <h2 className="text-sm font-semibold text-fg">{title}</h2>
          <ContentTypeBadge contentType={attractor.contentType} />
        </div>

        {attractor.linkedPromotionName ? (
          <LinkedPromotionBadge
            contentType={attractor.contentType}
            name={attractor.linkedPromotionName}
          />
        ) : null}

        <p className="flex items-center gap-1.5 text-xs text-fg-muted">
          <Icon name="calendar" size="xs" />
          {formatDateRange(attractor.startDateEpoch, attractor.endDateEpoch)}
        </p>

        <DaySelector
          readOnly
          size="sm"
          value={daysFromVisibility(attractor.dayVisibility)}
          aria-label={`${title} active days`}
        />
      </CardContent>

      <CardFooter className="mt-auto flex items-center justify-between border-t border-border px-4 py-2">
        <Button
          variant="ghost"
          size="sm"
          startIcon={<Icon name="gear" size="xs" />}
          onClick={() => onEdit(attractor)}
          aria-label={`Edit settings for ${title}`}
        >
          Edit Settings
        </Button>
        <IconButton
          aria-label={`Delete ${title}`}
          onClick={() => onDelete(attractor)}
        >
          <Icon name="trash" size="sm" />
        </IconButton>
      </CardFooter>
    </Card>
  );
};

export default AttractorCampaignCard;
